import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import usePopover from 'hooks/usePopover';

import {
  openConfirmationDialogAction,
  openDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  deleteLoanConditionAction,
  getLoanBrokerConditionByApplicationIdAction,
  getLoanConditionByLoanIdAction,
  getLoanApplicationTasksAction,
  signOffLoanConditionAction,
  unsignOffLoanConditionAction,
} from 'store/loanApplications/actions';
import { selectLoanConditionById } from 'store/loanApplications/selectors';

import AddConditionTasksForm from '../../AddConditionTasksForm';
import { ConditionsTestIds } from '../../Conditions.testIds';
import EditConditionModal from '../../EditConditionModal';
import ConditionHistoryDrawer from '../ConditionHistoryDrawer';
import LinkConditionTasksDrawer from '../LinkConditionTasksDrawer';
import RejectConditionDrawer from '../RejectConditionDrawer';
import RejectionReasonModal from '../RejectionReasonModal';
import { SlaGroupInfoModal } from '../SlaGroupInfo';

import { getRowActionItems } from './RowActionButton.utils';

const RowActionButton = ({
  condition,
  isBrokerCompany,
  isLoanCanceled,
  isManageAllowed,
  isEditAllowed,
  isSignOffAllowed,
  isUnsignOffAllowed,
  isRejectAllowed,
  loanId,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [isLinkTasksOpen, setIsLinkTasksOpen] = useState(false);

  const {
    id,
    number,
    canonicalTitle,
    isDeleted,
    signedOff,
    canBeSignedOff,
    canBeUnsignedOff,
    canBeRejected,
    rejectedDate,
    rejectionReason,
    taskIds,
    slaGroup,
  } = condition;
  const isRejected = !!rejectedDate;

  const refreshConditions = useCallback(() => {
    const fetchConditions = isBrokerCompany
      ? getLoanBrokerConditionByApplicationIdAction({ applicationId })
      : getLoanConditionByLoanIdAction({ loanId, partialLoad: true, withDeleted: true });

    return Promise.all([dispatch(fetchConditions), dispatch(getLoanApplicationTasksAction())]);
  }, [applicationId, dispatch, isBrokerCompany, loanId]);

  const onSignOff = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        message: `Sign off condition #${number}?`,
        type: ConfirmationModalType.WARNING,
        onAcceptButtonName: 'Sign Off',
        onCancelButtonName: 'Cancel',
        onAccept: () => {
          setIsActionLoading(true);

          dispatch(signOffLoanConditionAction({ loanConditionId: id }))
            .then(refreshConditions)
            .then(() =>
              dispatch(
                showMessageAction({
                  message: `Condition #${number} signed off successfully.`,
                  variant: 'success',
                }),
              ),
            )
            .finally(() => setIsActionLoading(false));
        },
      }),
    );
  }, [dispatch, id, number, refreshConditions]);

  const onUnsignOff = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        message: `Unsign off condition #${number}?`,
        type: ConfirmationModalType.WARNING,
        onAcceptButtonName: 'Unsign Off',
        onCancelButtonName: 'Cancel',
        onAccept: () => {
          setIsActionLoading(true);

          dispatch(unsignOffLoanConditionAction({ loanConditionId: id }))
            .then(refreshConditions)
            .then(() =>
              dispatch(
                showMessageAction({
                  message: `Condition #${number} unsigned off.`,
                  variant: 'success',
                }),
              ),
            )
            .finally(() => setIsActionLoading(false));
        },
      }),
    );
  }, [dispatch, id, number, refreshConditions]);

  const onDelete = useCallback(() => {
    dispatch(
      openConfirmationDialogAction({
        message: `Delete condition #${number}?`,
        description: 'This action cannot be undone.',
        type: ConfirmationModalType.WARNING,
        onAcceptButtonName: 'Delete',
        onCancelButtonName: 'Cancel',
        onAccept: () => {
          setIsActionLoading(true);

          dispatch(deleteLoanConditionAction({ loanConditionId: id, taskIds }))
            .then(refreshConditions)
            .then(() =>
              dispatch(
                showMessageAction({
                  message: `Condition #${number} deleted.`,
                  variant: 'success',
                }),
              ),
            )
            .finally(() => setIsActionLoading(false));
        },
      }),
    );
  }, [dispatch, id, number, taskIds, refreshConditions]);

  const onEdit = useCallback(() => {
    setIsEditOpen(true);
  }, []);

  const onCloseEdit = useCallback(() => {
    setIsEditOpen(false);
  }, []);

  const onReject = useCallback(() => {
    setIsRejectOpen(true);
  }, []);

  const onCloseReject = useCallback(() => {
    setIsRejectOpen(false);
  }, []);

  const onInfo = useCallback(() => {
    dispatch(getLoanConditionByLoanIdAction({ loanId, withDeleted: true }));

    dispatch(
      openDialogAction({
        header: `${slaGroup?.name ?? ''} Condition SLA Group`,
        size: ModalSize.XL,
        body: () => (
          <SlaGroupInfoModal conditionId={id} loanConditionByIdSelector={selectLoanConditionById} />
        ),
      }),
    );
  }, [dispatch, id, loanId, slaGroup?.name]);

  const onRejectionReason = useCallback(() => {
    dispatch(
      openDialogAction({
        header: `Rejection Reason for Condition #${number}`,
        size: ModalSize.SMALL,
        body: () => <RejectionReasonModal rejectionReason={rejectionReason} />,
      }),
    );
  }, [dispatch, number, rejectionReason]);

  const onAddTasks = useCallback(() => {
    dispatch(
      openDialogAction({
        header: `Add Tasks to Condition #${number}`,
        size: ModalSize.XL,
        body: dialogProps => <AddConditionTasksForm loanConditionId={id} {...dialogProps} />,
      }),
    );
  }, [dispatch, id, number]);

  const onLinkTasks = useCallback(() => {
    setIsLinkTasksOpen(true);
  }, []);

  const onCloseLinkTasks = useCallback(() => {
    setIsLinkTasksOpen(false);
  }, []);

  const onHistory = useCallback(() => {
    setIsHistoryOpen(true);
  }, []);

  const onCloseHistory = useCallback(() => {
    setIsHistoryOpen(false);
  }, []);

  const items = useMemo(
    () =>
      getRowActionItems({
        isActionLoading,
        isLoanCanceled,
        isDeleted,
        isRejected,
        rejectionReason,
        signedOff,
        canBeSignedOff,
        canBeUnsignedOff,
        canBeRejected,
        isSignOffAllowed,
        isUnsignOffAllowed,
        isRejectAllowed,
        isManageAllowed,
        canAddTasks: !isBrokerCompany,
        isEditAllowed,
        isBrokerCompany,
        onSignOff,
        onUnsignOff,
        onReject,
        onEdit,
        onDelete,
        onInfo,
        onAddTasks,
        onLinkTasks,
        onRejectionReason,
        onHistory,
      }),
    [
      isActionLoading,
      isLoanCanceled,
      isDeleted,
      isRejected,
      rejectionReason,
      signedOff,
      canBeSignedOff,
      canBeUnsignedOff,
      canBeRejected,
      isSignOffAllowed,
      isUnsignOffAllowed,
      isRejectAllowed,
      isManageAllowed,
      isBrokerCompany,
      isEditAllowed,
      onSignOff,
      onUnsignOff,
      onReject,
      onEdit,
      onDelete,
      onInfo,
      onAddTasks,
      onLinkTasks,
      onRejectionReason,
      onHistory,
    ],
  );

  const conditionTitle = canonicalTitle ?? `#${number}`;

  return (
    <>
      <MoreButtonCDS
        data-testid={ConditionsTestIds.ROW_ACTION_BUTTON}
        disabled={!items.length || isActionLoading}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />
      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
      <ConditionHistoryDrawer
        conditionId={id}
        conditionTitle={conditionTitle}
        open={isHistoryOpen}
        onClose={onCloseHistory}
      />
      {isEditOpen && (
        <EditConditionModal
          condition={condition}
          isBrokerCompany={isBrokerCompany}
          isEditAllowed={isEditAllowed}
          isManageAllowed={isManageAllowed}
          loanId={loanId}
          open={isEditOpen}
          onClose={onCloseEdit}
        />
      )}
      {isLinkTasksOpen && (
        <LinkConditionTasksDrawer
          condition={condition}
          loanId={loanId}
          open={isLinkTasksOpen}
          onClose={onCloseLinkTasks}
        />
      )}
      {isRejectOpen && (
        <RejectConditionDrawer
          condition={condition}
          isBrokerCompany={isBrokerCompany}
          loanId={loanId}
          open={isRejectOpen}
          onClose={onCloseReject}
        />
      )}
    </>
  );
};

RowActionButton.propTypes = {
  condition: PropTypes.object.isRequired,
  isBrokerCompany: PropTypes.bool.isRequired,
  isLoanCanceled: PropTypes.bool.isRequired,
  isManageAllowed: PropTypes.bool.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  isSignOffAllowed: PropTypes.bool.isRequired,
  isUnsignOffAllowed: PropTypes.bool.isRequired,
  isRejectAllowed: PropTypes.bool.isRequired,
  loanId: PropTypes.string.isRequired,
};

export default memo(RowActionButton);
